import type { MediaInfo, User } from '../types'

export const APP2 = 'https://app2.vaideo.org'

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

async function j<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const body = await res.json()
      if (body?.error) msg = body.error
    } catch { /* not json */ }
    throw new ApiError(res.status, msg)
  }
  return res.json() as Promise<T>
}

const opts: RequestInit = { credentials: 'include' }

export function loginUrl(): string {
  return `${APP2}/auth/login?next=${encodeURIComponent(window.location.href)}`
}

export async function getMe(): Promise<User | null> {
  const res = await fetch('/api/me', opts)
  if (res.status === 401) return null
  return j<User>(res)
}

export interface Uploaded { id: string; info: MediaInfo }

export function uploadMedia(file: File, onProgress: (f: number) => void): { promise: Promise<Uploaded>; abort: () => void } {
  const xhr = new XMLHttpRequest()
  const promise = new Promise<Uploaded>((resolve, reject) => {
    xhr.open('POST', '/api/media/upload')
    xhr.withCredentials = true
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total) }
    xhr.onload = () => {
      let body: { error?: string } & Partial<Uploaded> = {}
      try { body = JSON.parse(xhr.responseText) } catch { /* ignore */ }
      if (xhr.status >= 200 && xhr.status < 300 && body.id && body.info) resolve(body as Uploaded)
      else reject(new ApiError(xhr.status, body.error || `Error ${xhr.status} al subir`))
    }
    xhr.onerror = () => reject(new ApiError(0, 'Error de red al subir'))
    xhr.onabort = () => reject(new ApiError(0, 'Subida cancelada'))
    const fd = new FormData()
    fd.append('file', file)
    xhr.send(fd)
  })
  return { promise, abort: () => xhr.abort() }
}

export const mediaFileUrl = (id: string) => `/api/media/${id}/file`
export const mediaThumbUrl = (id: string, time = 1, width = 240) => `/api/media/${id}/thumbnail?time=${time}&width=${width}`
export const mediaStripUrl = (id: string, count: number, frameWidth: number) =>
  `/api/media/${id}/thumbnail-strip?count=${count}&frameWidth=${frameWidth}`

const waveCache = new Map<string, Promise<Int16Array>>()
export function getWaveform(id: string, peaks: number): Promise<Int16Array> {
  const key = `${id}:${peaks}`
  let p = waveCache.get(key)
  if (!p) {
    p = fetch(`/api/media/${id}/waveform?peaks=${peaks}`, opts)
      .then((r) => { if (!r.ok) throw new Error('waveform'); return r.arrayBuffer() })
      .then((b) => new Int16Array(b))
    p.catch(() => waveCache.delete(key))
    waveCache.set(key, p)
  }
  return p
}

// ---- projects ----------------------------------------------------------

export interface ProjectSummary {
  id: string
  name: string
  updatedAt: string
  sizeBytes: number
  durationSec?: number | null
  thumbnailUrl?: string | null
}

export async function listProjects(): Promise<ProjectSummary[]> {
  const r = await j<{ projects: ProjectSummary[] }>(await fetch('/api/projects', opts))
  return r.projects
}

export async function loadProject(id: string): Promise<{ id: string; name: string; data: unknown }> {
  return j(await fetch(`/api/projects/${encodeURIComponent(id)}`, opts))
}

export async function saveProject(body: { id?: string; name: string; data: unknown; thumbnailDataUrl?: string }): Promise<{ id: string; updatedAt: string }> {
  return j(await fetch('/api/projects/save', {
    ...opts, method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }))
}

export async function deleteProject(id: string): Promise<void> {
  await j(await fetch(`/api/projects/${encodeURIComponent(id)}`, { ...opts, method: 'DELETE' }))
}

// ---- Vaideo (app2) -----------------------------------------------------

export interface VaideoScene { idx: number; startSec: number; endSec: number; durationSec: number; narration: string }
export interface VaideoImport {
  video: Uploaded
  audio?: Uploaded | null
  scenes: VaideoScene[]
  sourceTitle: string
  language?: string | null
  aspectRatio?: string | null
  durationSec?: number | null
  burnedSubtitles?: boolean
}

export async function importFromVaideo(projectUid: string): Promise<VaideoImport> {
  return j(await fetch('/api/import/from-vaideo', {
    ...opts, method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectUid }),
  }))
}

export interface VaideoProjectSummary {
  uid: string
  title: string
  status: string
  aspectRatio?: string
  durationSec?: number
  createdAt?: string
  hasVideo: boolean
  thumbnailUrl?: string | null
}

export async function listVaideoProjects(): Promise<VaideoProjectSummary[]> {
  const r = await j<{ projects: VaideoProjectSummary[] }>(await fetch(`${APP2}/api/projects/`, opts))
  return r.projects
}

// ---- export ------------------------------------------------------------

export interface ExportStart { jobId: string; progressUrl: string; downloadUrl: string }
export interface ExportProgress {
  jobId: string; frame: number; fps: number; outTimeSec: number; speed: number
  progress: number; done: boolean; error: string | null; downloadUrl: string | null
}

export async function startExport(req: unknown): Promise<ExportStart> {
  return j(await fetch('/api/export', {
    ...opts, method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(req),
  }))
}

export async function cancelExport(jobId: string): Promise<void> {
  await fetch(`/api/export/${jobId}/cancel`, { ...opts, method: 'POST' })
}

// ---- voice (TTS through app2) ------------------------------------------

export interface VoiceOption {
  id: string; name: string; gender: 'male' | 'female'; genderLabel: string; age: string; ageLabel: string
  accent: string; style: string; tier: 'free' | 'pro'; locked: boolean; previewUrl: string | null
}
export interface VoiceCatalog {
  lang: string; plan: string; proAllowed: boolean; proActive: boolean; defaultVoice: string
  maxChars: number; perDay: number; usedToday: number; hasMusic: boolean; voices: VoiceOption[]
}
export interface VoiceWord { text: string; start: number; end: number }
export interface GeneratedVoice {
  voice: Uploaded
  words: VoiceWord[]
  voiceInfo: { id: string; name: string; tier: string }
  fellBack: boolean
  music: (Uploaded & { volume: number }) | null
}

export async function getVoices(lang: string, vaideoUid?: string): Promise<VoiceCatalog> {
  return j(await fetch(`/api/voice/voices?lang=${encodeURIComponent(lang)}&uid=${encodeURIComponent(vaideoUid ?? '')}`, opts))
}

export async function generateVoice(body: { text: string; lang: string; voiceId: string; vaideoUid?: string }): Promise<GeneratedVoice> {
  return j(await fetch('/api/voice/generate', {
    ...opts, method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }))
}
